import React from 'react';
import {filter as _filter, get as _get, isNil as _isNil, has as _has, isUndefined as _isUndefined, map as _map} from 'lodash';
import moment from 'moment/moment';
import {Icon, List, Popup} from 'semantic-ui-react';

import {PRODUCT_LCO, RTS_COVERAGE_CONFIRMED} from '@constants/variables';
import Chip from '@ui/Chip';
import {getSportNameFromEvent} from '@utils/sport';
import {getTournamentCategoryNameFromEvent, getTournamentNameFromEvent} from '@utils/tournament';
import {isValidMatchStatusId} from '@modules/liveControl/utils/helpers';
import {renderEventStatusName} from '@utils/status';
import {convertToInt} from '@utils/helpers';
import {IconAdd, IconEdit, IconLinkify} from '@appComponents/IconCollection';
import {EventContentVariantPopup} from '@utils/eventContentVariant/EventContentVariantPopup';
import Link, {getLink} from '@appComponents/Link';
import * as RESOURCES from '@constants/resources';
import {Authorization} from '@appComponents/Authorization';
import {eventAddAuthorizationObject, eventReadAuthorizationObject} from '@modules/events/components/Table/EventSchedule/authorizationObject';
import {CATEGORY_PATHS} from '@modules/events/utils/categoryPaths';
import {ShowWarnings} from '@utils/ShowWarnings';
import {renderDescription} from '@utils/tableHelpers';

const getPropertyNameHandler = (event, fn, propertyName) => {
    let result = fn(event);

    // @TODO: properly refactor getTournamentCategoryNameFromEvent, getTournamentNameFromEvent, getSportNameFromEvent
    // methods to get rid of annoying minus
    if ('-' === result) {
        result = _get(event, propertyName, '-');
    }

    return result;
};

export const checkIfIsEvent = (event) => (_has(event, 'sr_event.id') || _has(event, 'av_event.id') || _has(event, 'st_event.id'));

export const checkIfIsTvStreaming = (event) => {
    const path = checkIfIsEvent(event) ? 'sr_event.match.is_tv_streaming' : 'is_tv_streaming';

    return Boolean(_get(event, path, false));
};

export const getSportName = (event) => (
    getPropertyNameHandler(event, getSportNameFromEvent, 'av_event.content_category_level1.name')
);

export const getTournamentCategoryName = (event) => (
    getPropertyNameHandler(event, getTournamentCategoryNameFromEvent, 'av_event.content_category_level2.name')
);

export const getTournamentName = (event) => (
    getPropertyNameHandler(event, getTournamentNameFromEvent, 'av_event.content_category_level3.name')
);

export const getTvStreamingEncoderId = (event) => {
    const path = checkIfIsEvent(event) ? 'sr_event.match.tv_streaming_encoder.id' : 'tv_streaming_encoder.id';

    return _get(event, path, null);
};

export const getCompetitorWarnings = (event) => {
    const {match_status_id, match_status_name} = getMatchStatusData(event);
    let competitorWarnings = [],
        match = event;

    if (checkIfIsEvent(event) && _has(event, 'sr_event.match.id')) {
        match = event.sr_event.match;
    }

    if (!_has(match, 'home_competitor.id')) {
        competitorWarnings.push('No unique home competitor');
    }

    if (!_has(match, 'away_competitor.id')) {
        competitorWarnings.push('No unique away competitor');
    }

    if ((_has(match, 'home_competitor.is_excluded') && match.home_competitor.is_excluded)
        || (_has(match, 'sr_event.home_competitor.is_excluded') && match.sr_event.home_competitor.is_excluded)) {
        competitorWarnings.push('Excluded home competitor');
    }

    if ((_has(match, 'away_competitor.is_excluded') && match.away_competitor.is_excluded)
        || (_has(match, 'sr_event.away_competitor.is_excluded') && match.sr_event.away_competitor.is_excluded)) {
        competitorWarnings.push('Excluded away competitor');
    }

    if (_has(match, 'rts_coverage.id') && parseInt(match.rts_coverage.id, 10) === RTS_COVERAGE_CONFIRMED) {
        competitorWarnings.push('RTS');
    }

    if (!match.has_live_scout) {
        competitorWarnings.push('No scout');
    }

    if (!match.has_live_odds) {
        competitorWarnings.push('No Live Odds');
    }

    if (match.has_underage_competitors) {
        competitorWarnings.push('Underage competitors');
    }

    if (match.is_cancelled) {
        competitorWarnings.push('Cancelled');
    }

    if (match.is_postponed) {
        competitorWarnings.push('Postponed');
    }

    if (match.is_walkover) {
        competitorWarnings.push('Walkover');
    }

    if (isValidMatchStatusId(match_status_id)) {
        competitorWarnings.push(match_status_name);
    }

    if (match.match_schedule_state) {
        competitorWarnings.push(match.match_schedule_state);
    }

    return competitorWarnings;
};

export const getMatchStatusData = (data) => {
    if (_has(data, 'sr_event.match')) {
        return {
            match_status_id: data.sr_event.match?.match_status_id,
            match_status_name: data.sr_event.match?.match_status_name,
        };
    }

    return {
        match_status_id: data.match_status_id || null,
        match_status_name: data.match_status_name || null,
    };
};

export const isHomeCompetitorExcluded = (event) => {
    return Boolean(_get(event, 'sr_event.match.home_competitor.is_excluded',
        _get(event, 'sr_event.home_competitor.is_excluded',
            _get(event, 'home_competitor.is_excluded', false)
        )
    ));
};

export const isAwayCompetitorExcluded = (event) => {
    return Boolean(_get(event, 'sr_event.match.away_competitor.is_excluded',
        _get(event, 'sr_event.away_competitor.is_excluded',
            _get(event, 'away_competitor.is_excluded', false)
        )
    ));
};

export const filteredSelectedTournamentCategory = (selectedSports, prevState, setState) => {
    if (selectedSports && prevState.selectedSports && selectedSports !== prevState.selectedSports) {
        setState(prevState => ({
            selectedTournamentCategories: prevState.selectedTournamentCategories
                ?.filter(selectedTournamentCategory => prevState.tournamentCategories
                    .some(tournamentCategory => tournamentCategory.value === selectedTournamentCategory)),
        }));
    }
};

export const filteredSelectedTournament = (prevState, setState, tournamentsState, selectedTournaments) => {
    if (tournamentsState !== prevState.tournaments) {
        setState(() => ({
            selectedTournaments: selectedTournaments
                ?.filter(selectedTournament => tournamentsState
                    .some(item => item.value === selectedTournament)),
        }));
    }
};

export const allEventsHasLcoDistribution = (selectedEventsData) => {
    return selectedEventsData.every(event =>
        event.event_contents.some(content =>
            content.products.some(product => PRODUCT_LCO === product.product_id)
        )
    );
};

export const getSortingValue = (value) => {
    if ('object' === typeof value) {
        if (null === value) {
            return '';
        } else if (value.length) {
            return value.join();
        }

        return value.toString();
    }

    return value;
};

export const rowContainsColumn = (columns, row) => {
    if (!Array.isArray(columns)) {
        columns = [columns];
    }

    if (!row || 'object' !== typeof row) return false;

    return columns.some(column => !_isUndefined(row[column]) && null !== row[column]);
};

export const renderStatusColumn = (column, row) => {
    const renderedStatus = rowContainsColumn(column, row)
        ? renderEventStatusName(row.status_id, row.status)
        : '';

    return <div>{renderedStatus}</div>;
};

export const renderEventIdColumn = (column, row) => {
    return (
        <div>
            {rowContainsColumn(column, row) ? convertToInt(row[column]) : ''}
        </div>
    );
};

export const renderIdColumn = (column, row) => {
    const columnContent = (row.is_sr_event || row.is_st_event)
        ? <IconLinkify color='red'/>
        : '';

    return (
        <div>
            {rowContainsColumn(column, row) ? convertToInt(row[column]) : columnContent}
        </div>
    );
};

export const renderStartDatetimeColumn = (column, row) => {
    return (
        <div>
            <div>
                {rowContainsColumn(column, row) ? moment(row[column]).format('YYYY-MM-DD HH:mm') : ''}
            </div>
            <div>
                {rowContainsColumn('end_datetime', row) ? moment(row.end_datetime).format('YYYY-MM-DD HH:mm') : ''}
            </div>
        </div>
    );
};

export const renderCompetitorsColumn = (column, row) => {
    const competitorsWarningsExist = row.competitors_warnings && 0 !== row.competitors_warnings.length,
        hasArgosJobScheduling = row?.has_argos_job_scheduling && row?.is_sr_event && row?.match_id,
        hasSpiideoIntegration = row?.has_spiideo_integration && row?.is_sr_event && row?.match_id,
        tvStreamingInfoExist = row.is_tv_streaming && null !== row.tv_streaming_encoder_id,
        marginClass = (row.description && (competitorsWarningsExist || tvStreamingInfoExist || hasArgosJobScheduling || hasSpiideoIntegration))
            ? 'marginBetweenDescriptionAndWarnings'
            : '';

    let descriptionContent = rowContainsColumn('description', row)
        && renderDescription(row.description, row.color, row.external_description);

    return (
        <div>
            <div>
                {rowContainsColumn(column, row) ? row[column] : ''}
            </div>
            <div>
                {row.is_st_event || row.stage_id ? row.stage_name_with_tooltip : null}
            </div>
            <div>
                {descriptionContent}
            </div>
            <div className={marginClass}>
                {hasArgosJobScheduling && <Chip color='default' size='small' label='Argos'/>}
                {hasSpiideoIntegration && <Chip color='default' size='small' label='Spiideo'/>}
                {tvStreamingInfoExist && <Chip color='default' size='small' label='TV Streaming'/>}
                {competitorsWarningsExist && row.match_id && <ShowWarnings warnings={row.competitors_warnings} />}
            </div>
        </div>
    );
};

export const renderEventContentWarning = (content) => (
    <Popup
        trigger={<Icon className='warningSign' name='warning sign' color='red'/>}
        content={content.eventContentWarning.content}
        size='small'
        hoverable
        key={`content.popup.${content.event_content_id}`}
    />
);

export const renderContentColumns = (column, row) => {
    const renderedIds = new Set();

    if (!row[column] || 0 === row[column].length) {
        return null;
    }

    return (
        <List>
            {_map(row[column], (item, index) => {
                if (!renderedIds.has(item.event_content_id)) {
                    renderedIds.add(item.event_content_id);
                    const itemCount = _filter(row[column], {event_content_id: item.event_content_id}).length;

                    return (
                        <List.Item key={`${column}.list.item.${item.event_content_id}_${index}`}
                            className='eventContentDetails' style={{'--item-count': itemCount}}>
                            <List.Content>
                                <p>
                                    {item.name || '\u00A0'}
                                    {item.eventContentWarning && renderEventContentWarning(item)}
                                    {item.eventContentVariant &&
                                        <EventContentVariantPopup eventContentVariant={item.eventContentVariant}/>}
                                </p>
                            </List.Content>
                        </List.Item>
                    );
                } else {
                    return null;
                }
            })}
        </List>
    );
};

export const renderProductsAndDistribution = (column, row, fieldName = 'name') => {
    const lastElementIndices = new Map();
    row[column].forEach((item, index) => {
        lastElementIndices.set(item.event_content_id, index);
    });

    return (
        (!row[column] || 0 === row[column].length) ? null :
            <List>
                {_map(row[column], (item, index) => {
                    const product = row.products ? row.products[index] : null,
                        isLastInGroup = lastElementIndices.get(item.event_content_id) === index;

                    return (
                        <List.Item key={`${column}.list.item.${item.event_content_id}_${index}`}
                            className={isLastInGroup ? 'lastInGroup' : ''}>
                            <List.Content key={`${column}.list.content.${item.event_content_id}`}>
                                <p key={`${column}.p.${item.product_id}`}>
                                    {item[fieldName] || (product ? product.short_name : '\u00A0')}
                                </p>
                            </List.Content>
                        </List.Item>
                    );
                })}
            </List>
    );
};

export const renderColumnWithList = (column, row, defaultValue = 0, fieldName = false) => {
    let field = column;

    if (fieldName) {
        field = fieldName;
    }

    const lastElementIndices = new Map();
    row[column].forEach((item, index) => {
        lastElementIndices.set(item.event_content_id, index);
    });

    return (
        (!row[column] || 0 === row[column].length) ? null:
            <List>
                {_map(row[column], (item, index) => {
                    const isLastInGroup = lastElementIndices.get(item.event_content_id) === index;
                    const columnValues = _filter(row[column], {
                        event_content_id: row.products[index].event_content_id,
                        product_id: row.products[index].product_id,
                    });

                    return (
                        <List.Item
                            key={`${field}.list.item.${item.event_content_id}_${index}`}
                            className={isLastInGroup ? 'lastInGroup' : ''}
                        >
                            <List.Content key={`${field}.list.content.${item.event_content_id}`}>
                                {0 === columnValues.length ? <p key={`product.p.${item.product_id}`}>0</p> :
                                    <p key={`product.p.${item.product_id}`}>
                                        {columnValues[0][field] || defaultValue}
                                    </p>
                                }
                            </List.Content>
                        </List.Item>
                    );
                })}
            </List>
    );
};

export const renderCategoryOrTournament = (column, row) => {
    return Array.isArray(row[column]) && 0 < row[column].length
        ? <List>
            {_map(row[column], (data, index) => {
                return (
                    <List.Item key={`tournament.list.item.${index}`}>
                        <List.Content key={`tournament.list.content.${index}`}>
                            <p key={`tournament.p.${index}`}>
                                {data}
                            </p>
                        </List.Content>
                    </List.Item>
                );
            })}
        </List>
        : row[column] || null;
};

export const renderVenueOrCourtAndTvChannelColumn = (column, row) => {
    return (
        <div>
            <div>
                {_isNil(row[column])
                    ? ''
                    : row[column]
                }
            </div>
            <div>
                {_isNil(row.tv_channel)
                    ? ''
                    : row.tv_channel
                }
            </div>
        </div>
    );
};

export const renderCoverageValue = (column, row) => {
    return (_isUndefined(row[column]) || null === row[column]) ? '' : row[column];
};

export const getAddEventLinkProps = (row) => {
    if (row.stage_id) {
        return {
            name: 'events.add.stage',
            params: {stageId: row.stage_id},
        };
    }

    return {
        name: 'events.add.sportradarExisting',
        params: {matchId: row.match_id},
    };
};

export const renderAction = (row) => {
    if (null !== row.event_id) {
        return (
            <Authorization authorization={eventReadAuthorizationObject}>
                <IconEdit
                    size='large'
                    link={getLink('events.configuration.index', {id: row.event_id})}
                    resource={RESOURCES.EVENT}
                    editIconName='setting'
                />
            </Authorization>
        );
    }

    const {name, params} = getAddEventLinkProps(row);

    return (
        <Authorization authorization={eventAddAuthorizationObject}>
            <Link name={name} params={params}>
                <IconAdd size='large'/>
            </Link>
        </Authorization>
    );
};

export const getClipProvidersNames = (providers = []) => {
    if (!providers?.length) return '';

    const providerMap = new Map();
    providers.forEach((provider) => {
        const providerName = provider.clip_provider_name ?? provider.clip_provider?.name;
        const contentTypeName = provider.content_type_name ?? provider.event_content_type?.name;

        if (!providerName || !contentTypeName) {
            return;
        }

        if (!providerMap.has(providerName)) {
            providerMap.set(providerName, new Set());
        }

        providerMap.get(providerName).add(contentTypeName);
    });

    const providerNames = Array.from(providerMap.keys()).sort((a, b) => a.localeCompare(b));

    const result = providerNames.map((providerName) => {
        const types = Array.from(providerMap.get(providerName)).sort((a, b) => a.localeCompare(b));

        return `${providerName} (${types.join(', ')})`;
    });

    return result.join(', ');
};

export const getTournamentAndSportIds = (events) => {
    const tournamentIds = new Set();
    const sportIds = new Set();

    for (const event of events) {
        const tournament = event.sr_event?.match?.tournament ?? event.sr_event?.tournament;

        if (tournament) {
            tournamentIds.add(convertToInt(tournament.id));

            const sport = tournament.tournament_category?.sport;

            if (sport) sportIds.add(convertToInt(sport.id));
        }
    }

    return {
        tournamentIds: [...tournamentIds],
        sportIds: [...sportIds],
    };
};

export const isEventOfType = (data, eventKey) => {
    return checkIfIsEvent(data) && null !== data[eventKey];
};

export const getEntityId = (data, nestedPath, allowFallback = true) => {
    if (_has(data, nestedPath)) {
        return convertToInt(_get(data, nestedPath));
    }

    if (!checkIfIsEvent(data) && allowFallback && data?.id) {
        return convertToInt(data.id);
    }

    return null;
};

export const generateRowId = (data, isSrEvent) => {
    const prefix = checkIfIsEvent(data) ? 'event_' : 'match_';

    return `${prefix}${convertToInt(data.id)}_${Number(isSrEvent)}`;
};

export const getMatchOrStageId = (data, hasStageType = false, isStEvent = false) => {
    return !hasStageType && !isStEvent ? getEntityId(data, 'sr_event.match.id') : getEntityId(data, 'st_event.stage.id');
};

export const getStagesDescription = (data) => {
    if (!isEventOfType(data, 'st_event') && !_has(data, 'stage_type_id')) {
        return checkIfIsEvent(data) ? data.description : null;
    }

    const stageName = data.st_event?.stage?.name
        || data.st_event?.name
        || data.name
        || '';

    const stageDescription = data.st_event?.stage
        ? data.st_event.stage.description
        : data.st_event?.description || data.description || '';

    if (stageName && stageDescription) {
        return `${stageName} (${stageDescription})`;
    }

    return stageName || stageDescription || null;
};

export const getStageEventNameWithTooltip = (data = null) => {
    if (!data) return null;

    const content = data.st_event?.stage?.id ? data.st_event.stage : (data.st_event || data);

    if (!content.name) return null;

    const {name, description} = content;

    if (!description) return name;

    return (
        <Popup
            trigger={<span>{name}</span>}
            position='top left'
            hoverable
            content={description}
        />
    );
};

export const checkCategoryType = (data, isEvent) => {
    if (isEvent) {
        if (_get(data, 'sr_event.match.tournament')) return 'sr_match';

        if (_get(data, 'sr_event.tournament')) return 'sr_tournament';

        if (_get(data, 'st_event.stage')) return 'st_stage';

        if (_get(data, 'st_event.stage_category')) return 'st_stage_category';

        if (_get(data, 'av_event')) return 'av_event';
    } else {
        if (_get(data, 'tournament')) return 'tournament';

        if (_get(data, 'stage_category')) return 'stage_category';
    }

    return null;
};

// Extract values based on event category type and level
export const extractCategoryValue = (data, categoryType, paths, level, suffix) => {
    const config = paths[categoryType];

    if (!config) return null;

    if (config.dynamic) {
        const key = `${suffix}${level}`;

        return _get(data, `${config.base}.${key}.name`, null);
    }

    const path = config.paths[level];

    if (!path) return null;

    return _get(data, `${config.base}.${path}`, null);
};

export const getCategoryLevel = (data, level) => {
    const isEvent = checkIfIsEvent(data);
    const categoryType = checkCategoryType(data, isEvent);

    if (!categoryType) return null;

    return extractCategoryValue(data, categoryType, CATEGORY_PATHS, level, 'content_category_level');
};

export const getVenueOrCourtByMatchOrStage = (data) => _get(data, 'stadium.name', null);

export const getVenueOrCourt = (data) => {
    if (!checkIfIsEvent(data)) {
        return getVenueOrCourtByMatchOrStage(data);
    }

    const paths = [
        'sr_event.match',
        'sr_event',
        'st_event.stage',
        'st_event',
        'av_event.venue',
    ];

    for (const path of paths) {
        const content = _get(data, path);

        if (!content) continue;

        const stadiumOrVenue = _get(content, 'stadium.name') ?? _get(content, 'venue.name');

        if (stadiumOrVenue) return stadiumOrVenue;
    }

    return null;
};

export const splitByEventStart = (items, eventStartDatetime) => {
    const before = [];
    const after = [];

    items.forEach((item) => {
        item.start_datetime < eventStartDatetime ? before.push(item) : after.push(item);
    });

    return {before, after};
};

export const appendRows = (rows, items, callback) => {
    items.forEach((item) => {
        rows.push(callback(item));
    });
};

export const prepareEventScheduleTableData = (eventSchedule, callback) => {
    let {events, matches, stages} = eventSchedule;
    const tableData = [];

    events.forEach((event) => {
        const matchSplit = splitByEventStart(matches, event.start_datetime);
        const stageSplit = splitByEventStart(stages, event.start_datetime);

        appendRows(tableData, matchSplit.before, callback);
        appendRows(tableData, stageSplit.before, callback);

        tableData.push(callback(event));

        matches = matchSplit.after;
        stages = stageSplit.after;
    });

    appendRows(tableData, matches, callback);
    appendRows(tableData, stages, callback);

    return tableData;
};
